<script setup>
import PhoneReportFilterSelector from './PhoneReportFilterSelector.vue';
import { useStore } from 'dashboard/composables/store';
import Table from 'dashboard/components/table/Table.vue';
import { generateFileName } from 'dashboard/helper/downloadHelper';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';
import { computed, onMounted, ref, h } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatTime } from '../constants';

const props = defineProps({
  actionKey: {
    type: String,
    required: true,
  },
  summaryKey: {
    type: String,
    required: true,
  },
  type: {
    type: String,
    default: 'phone',
  },
});

const store = useStore();
const { t } = useI18n();

const from = ref(0);
const to = ref(0);
const groupBy = ref('agent');

// Get phone reports data from store
const reportMetrics = computed(() => store.getters[props.summaryKey] || []);

const columnHelper = createColumnHelper();

const defaultSpanRender = cellProps =>
  h(
    'span',
    {
      class: cellProps.getValue() ? '' : 'text-n-slate-12',
    },
    cellProps.getValue()
  );

// Header component for columns with tooltips
const HeaderWithTooltip = {
  props: ['headerText', 'tooltipText'],
  template: `
    <div class="flex items-center content-center gap-1" v-tooltip.top="tooltipText">
      <span>
        {{ headerText }}&nbsp;
      </span>
      <div>
        <span class="i-lucide-info size-4"></span>
      </div>
    </div>
  `,
};

const columns = computed(() => {
  const nameHeader =
    groupBy.value === 'agent'
      ? t('SUMMARY_REPORTS.AGENT')
      : t('SUMMARY_REPORTS.INBOX');

  return [
    columnHelper.accessor('name', {
      header: nameHeader,
      width: 200,
      cell: defaultSpanRender,
    }),
    columnHelper.accessor('totalCallCount', {
      header: () =>
        h(HeaderWithTooltip, {
          headerText: t('PHONE_REPORTS.TABLE.TOTAL_CALLS'),
          tooltipText: t('PHONE_REPORTS.TOOLTIPS.TOTAL_CALLS'),
        }),
      width: 120,
      cell: defaultSpanRender,
    }),
    columnHelper.accessor('inboundCallCount', {
      header: () =>
        h(HeaderWithTooltip, {
          headerText: t('PHONE_REPORTS.TABLE.INBOUND_CALLS'),
          tooltipText: t('PHONE_REPORTS.TOOLTIPS.INBOUND_CALLS'),
        }),
      width: 120,
      cell: defaultSpanRender,
    }),
    columnHelper.accessor('outboundCallCount', {
      header: () =>
        h(HeaderWithTooltip, {
          headerText: t('PHONE_REPORTS.TABLE.OUTBOUND_CALLS'),
          tooltipText: t('PHONE_REPORTS.TOOLTIPS.OUTBOUND_CALLS'),
        }),
      width: 120,
      cell: defaultSpanRender,
    }),
    columnHelper.accessor('failedOutboundCallCount', {
      header: () =>
        h(HeaderWithTooltip, {
          headerText: t('PHONE_REPORTS.TABLE.FAILED_OUTBOUND_CALLS'),
          tooltipText: t('PHONE_REPORTS.TOOLTIPS.FAILED_OUTBOUND_CALLS'),
        }),
      width: 140,
      cell: defaultSpanRender,
    }),
    columnHelper.accessor('unansweredMissedCallCount', {
      header: () =>
        h(HeaderWithTooltip, {
          headerText: t('PHONE_REPORTS.TABLE.UNANSWERED_MISSED_CALLS'),
          tooltipText: t('PHONE_REPORTS.TOOLTIPS.UNANSWERED_MISSED_CALLS'),
        }),
      width: 120,
      cell: defaultSpanRender,
    }),
    columnHelper.accessor('unansweredMissedPercentage', {
      header: () =>
        h(HeaderWithTooltip, {
          headerText: t('PHONE_REPORTS.TABLE.UNANSWERED_MISSED_PERCENTAGE'),
          tooltipText: t('PHONE_REPORTS.TOOLTIPS.UNANSWERED_MISSED_PERCENTAGE'),
        }),
      width: 160,
      cell: defaultSpanRender,
    }),
    columnHelper.accessor('insufficientlyHandledUnansweredMissedCalls', {
      header: () =>
        h(HeaderWithTooltip, {
          headerText: t(
            'PHONE_REPORTS.TABLE.INSUFFICIENTLY_HANDLED_UNANSWERED_MISSED_CALLS'
          ),
          tooltipText: t(
            'PHONE_REPORTS.TOOLTIPS.INSUFFICIENTLY_HANDLED_UNANSWERED_MISSED_CALLS'
          ),
        }),
      width: 120,
      cell: defaultSpanRender,
    }),
    columnHelper.accessor('insufficientlyHandledUnansweredMissedPercentage', {
      header: () =>
        h(HeaderWithTooltip, {
          headerText: t(
            'PHONE_REPORTS.TABLE.INSUFFICIENTLY_HANDLED_UNANSWERED_MISSED_PERCENTAGE'
          ),
          tooltipText: t(
            'PHONE_REPORTS.TOOLTIPS.INSUFFICIENTLY_HANDLED_UNANSWERED_MISSED_PERCENTAGE'
          ),
        }),
      width: 160,
      cell: defaultSpanRender,
    }),
    columnHelper.accessor('averageAgentHandledCallDuration', {
      header: () =>
        h(HeaderWithTooltip, {
          headerText: t('PHONE_REPORTS.TABLE.AVG_AGENT_HANDLED_DURATION'),
          tooltipText: t('PHONE_REPORTS.TOOLTIPS.AVG_AGENT_HANDLED_DURATION'),
        }),
      width: 180,
      cell: defaultSpanRender,
    }),
    columnHelper.accessor('totalAgentHandledCallDuration', {
      header: () =>
        h(HeaderWithTooltip, {
          headerText: t('PHONE_REPORTS.TABLE.TOTAL_AGENT_HANDLED_DURATION'),
          tooltipText: t('PHONE_REPORTS.TOOLTIPS.TOTAL_AGENT_HANDLED_DURATION'),
        }),
      width: 180,
      cell: defaultSpanRender,
    }),
    columnHelper.accessor('averageRingingTime', {
      header: () =>
        h(HeaderWithTooltip, {
          headerText: t('PHONE_REPORTS.TABLE.AVG_RINGING_TIME'),
          tooltipText: t('PHONE_REPORTS.TOOLTIPS.AVG_RINGING_TIME'),
        }),
      width: 160,
      cell: defaultSpanRender,
    }),
    columnHelper.accessor('connectedOutboundCallPercentage', {
      header: () =>
        h(HeaderWithTooltip, {
          headerText: t('PHONE_REPORTS.TABLE.CONNECTED_OUTBOUND_PERCENTAGE'),
          tooltipText: t(
            'PHONE_REPORTS.TOOLTIPS.CONNECTED_OUTBOUND_PERCENTAGE'
          ),
        }),
      width: 180,
      cell: defaultSpanRender,
    }),
    columnHelper.accessor('uniqueCallerCount', {
      header: () =>
        h(HeaderWithTooltip, {
          headerText: t('PHONE_REPORTS.TABLE.UNIQUE_CALLERS'),
          tooltipText: t('PHONE_REPORTS.TOOLTIPS.UNIQUE_CALLERS'),
        }),
      width: 120,
      cell: defaultSpanRender,
    }),
    columnHelper.accessor('uniqueCalledContactCount', {
      header: () =>
        h(HeaderWithTooltip, {
          headerText: t('PHONE_REPORTS.TABLE.UNIQUE_CALLED_CONTACTS'),
          tooltipText: t('PHONE_REPORTS.TOOLTIPS.UNIQUE_CALLED_CONTACTS'),
        }),
      width: 140,
      cell: defaultSpanRender,
    }),
  ];
});

const renderCount = value => (value ? value.toLocaleString() : '--');
const renderTime = value => {
  if (value !== undefined && value !== null) {
    return formatTime(Number(value.toFixed()), true);
  }

  return '--';
};
const renderPercentage = value =>
  value !== undefined && value !== null ? `${value.toFixed()}%` : '--';

const tableData = computed(() =>
  reportMetrics.value.map(row => ({
    id: row.id,
    name: row.name,
    type: props.type,
    totalCallCount: renderCount(row.totalCallCount),
    inboundCallCount: renderCount(row.inboundCallCount),
    outboundCallCount: renderCount(row.outboundCallCount),
    failedOutboundCallCount: renderCount(row.failedOutboundCallCount),
    unansweredMissedCallCount: renderCount(row.unansweredMissedCallCount),
    unansweredMissedPercentage: renderPercentage(
      row.unansweredMissedPercentage
    ),
    insufficientlyHandledUnansweredMissedCalls: renderCount(
      row.insufficientlyHandledUnansweredMissedCalls
    ),
    insufficientlyHandledUnansweredMissedPercentage: renderPercentage(
      row.insufficientlyHandledUnansweredMissedPercentage
    ),
    averageAgentHandledCallDuration: renderTime(
      row.averageAgentHandledCallDuration
    ),
    totalAgentHandledCallDuration: renderTime(
      row.totalAgentHandledCallDuration
    ),
    averageRingingTime: renderTime(row.averageRingingTime),
    connectedOutboundCallPercentage: renderPercentage(
      row.connectedOutboundCallPercentage
    ),
    uniqueCallerCount: renderCount(row.uniqueCallerCount),
    uniqueCalledContactCount: renderCount(row.uniqueCalledContactCount),
  }))
);

const fetchAllData = () => {
  store.dispatch(props.actionKey, {
    since: from.value,
    until: to.value,
    groupBy: groupBy.value,
  });
};

onMounted(() => fetchAllData());

const onFilterChange = updatedFilter => {
  from.value = updatedFilter.from;
  to.value = updatedFilter.to;
  groupBy.value = updatedFilter.groupBy || groupBy.value;
  fetchAllData();
};

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  get columns() {
    return columns.value;
  },
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
});

// downloadReports method is exposed to be used in the parent component
const downloadReports = () => {
  const fileName = generateFileName({
    type: 'phone',
    to: to.value,
  });
  const params = {
    from: from.value,
    to: to.value,
    fileName,
    groupBy: groupBy.value,
  };
  store.dispatch('downloadPhoneReports', params);
};

defineExpose({ downloadReports });
</script>

<template>
  <PhoneReportFilterSelector @filter-change="onFilterChange" />
  <div
    class="flex-1 overflow-auto px-5 py-6 mt-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
  >
    <Table :table="table" />
  </div>
</template>
